import { useMemo } from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import type { ClusterOutlierPoint } from '@avisoma/shared';

export function ClusterOutlierList({
  points,
  hoveredId,
  onHover,
  onSelect,
}: {
  points: ClusterOutlierPoint[];
  hoveredId: number | null;
  onHover: (id: number | null) => void;
  onSelect: (id: number) => void;
}) {
  const outliers = useMemo(
    () => points.filter((point) => point.isOutlier).sort((a, b) => b.distance - a.distance),
    [points],
  );

  return (
    <div>
      <h3 className="text-sm font-medium text-gray-900 dark:text-zinc-100">
        Outlier clusters ({outliers.length})
      </h3>
      {outliers.length === 0 ? (
        <p className="mt-2 text-sm text-gray-500 dark:text-zinc-400">No outlier clusters detected.</p>
      ) : (
        <div className="mt-2 max-h-56 overflow-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-zinc-700 text-left text-xs text-gray-500 dark:text-zinc-400">
                <th className="py-1.5 pr-2 font-medium">Cluster</th>
                <th className="py-1.5 pr-2 font-medium">Pages</th>
                <th className="py-1.5 text-right font-medium">Distance</th>
              </tr>
            </thead>
            <tbody>
              {outliers.map((point) => (
                <tr
                  key={point.id}
                  className={clsx(
                    'cursor-pointer border-b border-gray-100 dark:border-zinc-800',
                    hoveredId === point.id && 'bg-gray-50 dark:bg-zinc-700/40',
                  )}
                  onMouseEnter={() => onHover(point.id)}
                  onMouseLeave={() => onHover(null)}
                  onClick={() => onSelect(point.id)}
                >
                  <td className="py-1.5 pr-2 text-gray-900 dark:text-zinc-100">
                    <span className="inline-flex items-center gap-1">
                      Cluster {point.id}
                      <Link
                        to={`/category-clusters/${point.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400"
                        aria-label={`Open cluster ${point.id} page`}
                      >
                        <ArrowTopRightOnSquareIcon className="size-3.5" />
                      </Link>
                    </span>
                  </td>
                  <td className="py-1.5 pr-2 text-gray-500 dark:text-zinc-400">{point.pageCount}</td>
                  <td className="py-1.5 text-right font-mono text-red-600 dark:text-red-400">
                    {point.distance.toFixed(3)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
